import {
  AgentThreadMessage,
  InvokeSkillSpec,
  SendMessageRequest,
} from "@/shared/domain/agentThread";
import { HttpClient, robotoHeaders } from "@/shared/http";
import { PaginatedResponse } from "@/shared/http/response";

import {
  CreateSkillRequest,
  CreateSkillVersionRequest,
  SetSkillSubscriptionRequest,
  SkillListScope,
  SkillRecord,
  SkillSubscriptionRecord,
  SkillSummary,
  SkillVersionRecord,
  UpdateSkillMetadataRequest,
  UpdateSkillVersionRequest,
} from "./SkillRecord";

interface Options {
  abortSignal?: AbortSignal;
  orgId?: string;
}

interface ListSkillsOptions extends Options {
  pageToken?: string;
  /** Server-side cap is 1000; values above that 400. */
  limit?: number;
  /**
   * Caller-relative slice: `"personal"` for authored or subscribed, `"org"`
   * for org-shared skills the caller did not author (regardless of
   * subscription state). Omit to get every visible skill.
   */
  scope?: SkillListScope;
}

export class SkillService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async listSkills(
    options?: ListSkillsOptions,
  ): Promise<PaginatedResponse<SkillSummary>> {
    const params = new URLSearchParams();
    if (options?.pageToken !== undefined) {
      params.set("page_token", options.pageToken);
    }
    if (options?.limit !== undefined) {
      params.set("limit", String(options.limit));
    }
    if (options?.scope !== undefined) {
      params.set("scope", options.scope);
    }
    const qs = params.toString();
    const url = this.#httpClient.constructUrl(
      qs ? `v1/skills?${qs}` : "v1/skills",
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
    });
    return response.json<PaginatedResponse<SkillSummary>>();
  }

  /**
   * Distinct tags across skills the caller can see in the active org.
   *
   * Filtered server-side by visibility — private skills owned by other users
   * contribute no tags. Suitable as a TagAndMetadataAutocomplete data source.
   */
  public async listKnownTags(options?: Options): Promise<string[]> {
    const url = this.#httpClient.constructUrl("v1/skills/tags");
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
    });
    return response.json<string[]>();
  }

  public async getSkillById(
    skillId: string,
    options?: Options,
  ): Promise<SkillRecord> {
    const url = this.#httpClient.constructUrl(`v1/skills/id/${skillId}`);
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
    });
    return response.json<SkillRecord>();
  }

  public async createSkill(
    request: CreateSkillRequest,
    options?: Options,
  ): Promise<SkillRecord> {
    const url = this.#httpClient.constructUrl("v1/skills/create");
    // Non-idempotent on the server (each POST mints a new skill_id), so a
    // transient 502 must not be silently retried into a duplicate row.
    const response = await this.#httpClient.post(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
      body: JSON.stringify(request),
      idempotent: false,
    });
    return response.json<SkillRecord>();
  }

  public async updateSkillMetadata(
    skillId: string,
    request: UpdateSkillMetadataRequest,
    options?: Options,
  ): Promise<SkillRecord> {
    const url = this.#httpClient.constructUrl(`v1/skills/id/${skillId}`);
    const response = await this.#httpClient.put(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
      body: JSON.stringify(request),
    });
    return response.json<SkillRecord>();
  }

  public async deleteSkill(skillId: string, options?: Options): Promise<void> {
    const url = this.#httpClient.constructUrl(`v1/skills/id/${skillId}`);
    const response = await this.#httpClient.delete(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
      // Avoid the silent-retry case where the first DELETE succeeded but
      // the response was lost: a retry would then 404 and surface a
      // confusing "not found" instead of the real success.
      idempotent: false,
    });
    await response.throwIfError();
  }

  public async listVersions(
    skillId: string,
    options?: Options,
  ): Promise<SkillVersionRecord[]> {
    const url = this.#httpClient.constructUrl(
      `v1/skills/id/${skillId}/versions`,
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
    });
    return response.json<SkillVersionRecord[]>();
  }

  public async getVersion(
    skillId: string,
    version: number,
    options?: Options,
  ): Promise<SkillVersionRecord> {
    const url = this.#httpClient.constructUrl(
      `v1/skills/id/${skillId}/versions/${version}`,
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
    });
    return response.json<SkillVersionRecord>();
  }

  public async createVersion(
    skillId: string,
    request: CreateSkillVersionRequest,
    options?: Options,
  ): Promise<SkillVersionRecord> {
    const url = this.#httpClient.constructUrl(
      `v1/skills/id/${skillId}/versions`,
    );
    // Non-idempotent on the server (each POST inserts MAX(version)+1), so a
    // transient 502 must not be silently retried into a duplicate version.
    const response = await this.#httpClient.post(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
      body: JSON.stringify(request),
      idempotent: false,
    });
    return response.json<SkillVersionRecord>();
  }

  public async updateVersion(
    skillId: string,
    version: number,
    request: UpdateSkillVersionRequest,
    options?: Options,
  ): Promise<SkillVersionRecord> {
    const url = this.#httpClient.constructUrl(
      `v1/skills/id/${skillId}/versions/${version}`,
    );
    const response = await this.#httpClient.put(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
      body: JSON.stringify(request),
    });
    return response.json<SkillVersionRecord>();
  }

  public async deleteVersion(
    skillId: string,
    version: number,
    options?: Options,
  ): Promise<void> {
    const url = this.#httpClient.constructUrl(
      `v1/skills/id/${skillId}/versions/${version}`,
    );
    const response = await this.#httpClient.delete(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
      idempotent: false,
    });
    await response.throwIfError();
  }

  /**
   * Subscribe the caller to a skill. Idempotent; visibility-gated.
   *
   * Creates the per-user state row with `ai_version = null` (subscribed but
   * AI-disabled). Use {@link setSubscription} to pin a version separately.
   */
  public async subscribe(
    skillId: string,
    options?: Options,
  ): Promise<SkillSubscriptionRecord> {
    const url = this.#httpClient.constructUrl(
      `v1/skills/id/${skillId}/subscribe`,
    );
    const response = await this.#httpClient.post(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
    });
    return response.json<SkillSubscriptionRecord>();
  }

  public async unsubscribe(skillId: string, options?: Options): Promise<void> {
    const url = this.#httpClient.constructUrl(
      `v1/skills/id/${skillId}/subscribe`,
    );
    const response = await this.#httpClient.delete(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
      idempotent: false,
    });
    await response.throwIfError();
  }

  /**
   * Pin (or clear) the caller's AI-available version for a skill.
   *
   * Upsert semantics: creates the subscription row if missing, otherwise
   * updates `ai_version` in place. Passing a stale version returns 404 — the
   * composite FK to `skill_versions` enforces existence on the server.
   */
  public async setSubscription(
    skillId: string,
    request: SetSkillSubscriptionRequest,
    options?: Options,
  ): Promise<SkillSubscriptionRecord> {
    const url = this.#httpClient.constructUrl(
      `v1/skills/id/${skillId}/subscribe`,
    );
    const response = await this.#httpClient.put(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
      body: JSON.stringify(request),
    });
    return response.json<SkillSubscriptionRecord>();
  }

  /**
   * Invoke one or more skills into an existing chat session.
   *
   * Posts to the standard send-message endpoint with `invoke_skills` set —
   * the dedicated /invoke-skill route was folded into /messages so the
   * frontend has a single mutation path for "add a turn trigger to this chat."
   * The chat composer can ship multiple chips alongside free-form user text
   * by passing more than one spec in `requests`.
   */
  public async invokeSkillsInChat(
    chatId: string,
    requests: InvokeSkillSpec[],
    options?: Options & { message?: AgentThreadMessage },
  ): Promise<void> {
    const url = this.#httpClient.constructUrl(`v1/ai/chats/${chatId}/messages`);
    const body: SendMessageRequest = {
      message: options?.message,
      invoke_skills: requests.map((r) => ({
        skill_id: r.skill_id,
        version: r.version,
      })),
    };
    // Non-idempotent: each call fabricates a new tool_use/tool_result pair
    // in the transcript. A silent 502 retry would duplicate the turn.
    const response = await this.#httpClient.post(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
      body: JSON.stringify(body),
      idempotent: false,
    });
    await response.throwIfError();
  }

  // For starting a new thread via skill invocation, callers go through
  // ``AgentThreadService.startAgentThread({ invokeSkills, ... })`` — there is no
  // skill-side "start thread" helper. Keeping all thread creation in one service
  // method means context, model profile, and client tools land on the wire the
  // same way regardless of whether the trigger is a typed message or a skill.
}
